import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare, X, Send, Minimize2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  requestNotificationPermission,
  prepareNotificationSound,
  notifyNewMessage,
} from "@/lib/webchatNotifications";

interface Message {
  id: string;
  content: string;
  sender_type: "user" | "contact" | "system";
  created_at: string;
}

// Renderiza texto com formatação estilo WhatsApp:
// \n (literal ou real) → quebra de linha
// - no início da linha → lista com marcador (•)
// *texto* → negrito
// _texto_ → itálico
// ~texto~ → riscado
function bulletLine(line: string): string {
  const t = line.trimStart();
  if (t.startsWith("- ")) return line.slice(0, line.length - t.length) + "• " + t.slice(2);
  return line;
}
function FormattedMessage({ text }: { text: string }) {
  const normalized = text.replace(/\\n/g, "\n");
  const lines = normalized.split("\n");

  function parseInline(line: string, lineIdx: number): React.ReactNode {
    const parts: React.ReactNode[] = [];
    const regex = /(\*([^*]+)\*)|(_([^_]+)_)|(~([^~]+)~)/g;
    let last = 0;
    let match: RegExpExecArray | null;
    let i = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > last) {
        parts.push(<Fragment key={`t${lineIdx}-${i}`}>{line.slice(last, match.index)}</Fragment>);
        i++;
      }
      if (match[1]) {
        parts.push(<strong key={`b${lineIdx}-${i}`}>{match[2]}</strong>);
      } else if (match[3]) {
        parts.push(<em key={`i${lineIdx}-${i}`}>{match[4]}</em>);
      } else if (match[5]) {
        parts.push(<del key={`s${lineIdx}-${i}`}>{match[6]}</del>);
      }
      last = match.index + match[0].length;
      i++;
    }
    if (last < line.length) {
      parts.push(<Fragment key={`t${lineIdx}-end`}>{line.slice(last)}</Fragment>);
    }
    return parts;
  }

  return (
    <span>
      {lines.map((line, idx) => (
        <Fragment key={idx}>
          {parseInline(bulletLine(line), idx)}
          {idx < lines.length - 1 && <br />}
        </Fragment>
      ))}
    </span>
  );
}

export function WebChatFloatingWidget() {
  const { currentOrg } = useOrganizationContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [webchatEnabled, setWebchatEnabled] = useState(false);
  const [modulesEnabled, setModulesEnabled] = useState<string[]>([]);
  const [conversationToken, setConversationToken] = useState<string | null>(null);
  const [webchatId, setWebchatId] = useState<string | null>(null);
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const sseRef = useRef<EventSource | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationTokenRef = useRef<string | null>(null);
  const isOpenRef = useRef(false);
  const openWidgetRef = useRef<() => void>(() => setIsOpen(true));
  const [currentPath, setCurrentPath] = useState<string>("");

  // Declarar funções primeiro (antes de serem usadas)
  const getCurrentModule = useCallback((path: string): string | null => {
    if (path.startsWith("/shop")) return "fluity_lanches";
    if (path.startsWith("/admin")) return "fluity_lanches";
    return null;
  }, []);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  const initializeSession = useCallback(async () => {
    if (!currentOrg?.id) return;

    try {
      // Buscar public_key
      const { data: settings } = await supabase
        .from("org_webchat_settings")
        .select("webchat_public_key")
        .eq("org_id", currentOrg.id)
        .single();

      if (!settings?.webchat_public_key) return;

      // Gerar/recuperar visitor_id
      let vid = localStorage.getItem("fluity_visitor_id");
      if (!vid) {
        vid = `v_${crypto.randomUUID().replace(/-/g, "")}`;
        localStorage.setItem("fluity_visitor_id", vid);
      }
      setVisitorId(vid);

      // Criar sessão
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const response = await fetch(
        `${supabaseUrl}/functions/v1/webchat-api/session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({
            org_public_key: settings.webchat_public_key,
            origin_domain: window.location.hostname,
            page_url: window.location.href,
            visitor_id: vid,
          }),
        }
      );

      const data = await response.json();
      if (data.conversation_token) {
        setConversationToken(data.conversation_token);
        conversationTokenRef.current = data.conversation_token;
        setWebchatId(data.webchat_id);
        localStorage.setItem("fluity_webchat_id", data.webchat_id);
        
        // Conectar SSE diretamente aqui para evitar dependência circular
        if (sseRef.current) {
          sseRef.current.close();
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
        const eventSource = new EventSource(
          `${supabaseUrl}/functions/v1/webchat-api/stream?token=${encodeURIComponent(data.conversation_token)}`
        );

        eventSource.onopen = () => {
          setSseConnected(true);
        };

        const sessionToken = data.conversation_token;
        requestNotificationPermission();

        eventSource.addEventListener("connected", () => {
          // Histórico via API (mesmo fluxo do embed externo; retorno do bot vem pelo SSE)
          const apiUrl = import.meta.env.VITE_SUPABASE_URL || "";
          if (!sessionToken) return;
          fetch(`${apiUrl}/functions/v1/webchat-api/messages?token=${encodeURIComponent(sessionToken)}`)
            .then((r) => r.json())
            .then((body) => {
              if (body.messages && body.messages.length > 0) {
                setMessages(
                  body.messages.map((m: { message_id: number; content: string; sender_type: string; created_at: string }) => ({
                    id: `msg_${m.message_id}`,
                    content: m.content || "",
                    sender_type: m.sender_type === "contact" ? "contact" : "user",
                    created_at: m.created_at,
                  }))
                );
                scrollToBottom();
              }
            })
            .catch(() => {});
        });

        eventSource.addEventListener("message.new", (e) => {
          const message = JSON.parse(e.data);
          const msgId = `msg_${message.message_id ?? Date.now()}`;
          const content = (message.content || "").trim();
          setMessages((prev) => {
            if (prev.some((m) => m.id === msgId)) return prev;

            return [
              ...prev,
              {
                id: msgId,
                content: message.content || "",
                sender_type: message.sender_type === "contact" ? "contact" : "user",
                created_at: message.created_at || new Date().toISOString(),
              },
            ];
          });
          scrollToBottom();
          if (!isOpenRef.current || !document.hasFocus()) {
            notifyNewMessage(message.content || "", "WebChat – Nova mensagem", () => openWidgetRef.current?.(), msgId);
          }
        });

        eventSource.addEventListener("error", (e) => {
          console.error("SSE Error:", e);
          setSseConnected(false);
        });

        sseRef.current = eventSource;
      }
    } catch (error) {
      console.error("Erro ao inicializar sessão:", error);
    }
  }, [currentOrg, scrollToBottom]);

  useEffect(() => {
    isOpenRef.current = isOpen;
    openWidgetRef.current = () => {
      prepareNotificationSound();
      setIsOpen(true);
    };
  }, [isOpen]);

  // Atualizar path quando mudar
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  // Cache para evitar requisições repetidas quando a tabela não existe
  const tableExistsRef = useRef<boolean | null>(null);
  const lastOrgIdRef = useRef<string | null>(null);

  // Carregar configurações
  useEffect(() => {
    const loadSettings = async () => {
      if (!currentOrg?.id) return;

      // Resetar cache se a organização mudou
      if (lastOrgIdRef.current !== currentOrg.id) {
        tableExistsRef.current = null;
        lastOrgIdRef.current = currentOrg.id;
      }

      // Se já sabemos que a tabela não existe, não fazer requisição
      if (tableExistsRef.current === false) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from("org_webchat_settings")
          .select("webchat_enabled, webchat_modules_enabled")
          .eq("org_id", currentOrg.id)
          .maybeSingle();

        // Se a tabela não existir (404) ou não houver dados, apenas retornar
        if (error) {
          // Se for erro 404 (tabela não existe) ou PGRST116/PGRST205 (não encontrado/tabela não existe), ignorar silenciosamente
          // Não logar nada para não poluir o console
          const isTableNotFoundError = 
            error.code === 'PGRST116' || 
            error.code === 'PGRST205' || 
            error.message?.includes('404') || 
            error.message?.includes('Could not find the table') ||
            error.message?.includes('org_webchat_settings');
          
          if (isTableNotFoundError) {
            // Marcar que a tabela não existe para evitar requisições futuras
            tableExistsRef.current = false;
            // Tabela não existe ainda - migration não foi executada
            // Retornar silenciosamente sem logar
            return;
          }
          // Apenas logar erros inesperados que não sejam relacionados à tabela não existir
          // Mas apenas em modo de desenvolvimento
          if (import.meta.env.DEV) {
            console.warn('Erro ao carregar configurações do WebChat:', error);
          }
          return;
        }

        // Se chegou aqui, a tabela existe
        tableExistsRef.current = true;

        if (data) {
          setWebchatEnabled(data.webchat_enabled || false);
          setModulesEnabled(data.webchat_modules_enabled || []);

          // Verificar se o módulo atual está habilitado
          const currentModule = getCurrentModule(currentPath);
          if (data.webchat_enabled && currentModule && data.webchat_modules_enabled?.includes(currentModule)) {
            // Inicializar sessão
            initializeSession();
          }
        }
      } catch (error: any) {
        // Ignorar erros silenciosamente para não quebrar a aplicação
        // Não logar se for erro relacionado à tabela não existir
        const isTableNotFoundError = 
          error?.code === 'PGRST116' || 
          error?.code === 'PGRST205' || 
          error?.message?.includes('404') || 
          error?.message?.includes('Could not find the table') ||
          error?.message?.includes('org_webchat_settings');
        
        if (!isTableNotFoundError && import.meta.env.DEV) {
          console.warn('Erro ao carregar configurações do WebChat:', error);
        }
      }
    };

    loadSettings();
  }, [currentOrg, currentPath, getCurrentModule, initializeSession]);

  const connectSSE = useCallback((token: string) => {
    if (sseRef.current) {
      sseRef.current.close();
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const eventSource = new EventSource(
      `${supabaseUrl}/functions/v1/webchat-api/stream?token=${encodeURIComponent(token)}`
    );

    eventSource.onopen = () => {
      setSseConnected(true);
    };

    eventSource.addEventListener("connected", () => {
      if (token) {
        fetch(`${supabaseUrl}/functions/v1/webchat-api/messages?token=${encodeURIComponent(token)}`)
          .then((r) => r.json())
          .then((body) => {
            if (body.messages && body.messages.length > 0) {
              setMessages(
                body.messages.map((m: { message_id: number; content: string; sender_type: string; created_at: string }) => ({
                  id: `msg_${m.message_id}`,
                  content: m.content || "",
                  sender_type: m.sender_type === "contact" ? "contact" : "user",
                  created_at: m.created_at,
                }))
              );
              scrollToBottom();
            }
          })
          .catch(() => {});
      }
    });

    eventSource.addEventListener("message.new", (e) => {
      const message = JSON.parse(e.data);
      const msgId = `msg_${message.message_id || Date.now()}`;
      const content = (message.content || "").trim();
      setMessages((prev) => {
        if (prev.some((m) => m.id === msgId)) return prev;
        return [
          ...prev,
          { id: msgId, content: message.content || "", sender_type: message.sender_type === "contact" ? "contact" : "user", created_at: message.created_at || new Date().toISOString() },
        ];
      });
      scrollToBottom();
      if (!isOpenRef.current || !document.hasFocus()) {
        notifyNewMessage(message.content || "", "WebChat – Nova mensagem", () => openWidgetRef.current?.(), `msg_${message.message_id || Date.now()}`);
      }
    });

    eventSource.addEventListener("error", (e) => {
      console.error("SSE Error:", e);
      setSseConnected(false);
    });

    sseRef.current = eventSource;
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || !conversationToken || sending) return;

    setSending(true);
    const messageContent = inputMessage.trim();
    setInputMessage("");

    // Adicionar mensagem localmente (otimista)
    const tempMessage: Message = {
      id: `temp_${Date.now()}`,
      content: messageContent,
      sender_type: "contact",
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempMessage]);
    scrollToBottom();

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const response = await fetch(
        `${supabaseUrl}/functions/v1/webchat-api/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${conversationToken}`,
          },
          body: JSON.stringify({
            type: "text",
            content: messageContent,
            client_message_id: `m_${Date.now()}`,
            context: {
              origin_domain: window.location.hostname,
              page_url: window.location.href,
            },
          }),
        }
      );

      const data = await response.json();
      if (!data.ok) {
        throw new Error(data.error || "Erro ao enviar mensagem");
      }

      // Mantém a mensagem otimista no widget; o stream do webchat não deve ecoar mensagens do cliente.
      // Isso evita "sumiço" da mensagem enviada e reduz risco de reclassificação incorreta no retorno.
    } catch (error: any) {
      console.error("Erro ao enviar mensagem:", error);
      // Remover mensagem temporária em caso de erro
      setMessages((prev) => prev.filter((m) => m.id !== tempMessage.id));
    } finally {
      setSending(false);
    }
  }, [inputMessage, conversationToken, sending]);

  /** Encerra o chat na tabela chats_instagram (closed_at, status_atd, etc.) ao fechar o widget ou sair da página */
  const closeChatOnServer = useCallback((token: string | null, notes?: string) => {
    if (!token) return;
    const apiUrl = import.meta.env.VITE_SUPABASE_URL || "";
    if (!apiUrl) return;
    const url = `${apiUrl}/functions/v1/webchat-api/close`;
    const body = notes ? JSON.stringify({ notes_closed: notes }) : "{}";
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body,
      keepalive: true,
    }).catch(() => {});
  }, []);

  useEffect(() => {
    conversationTokenRef.current = conversationToken;
  }, [conversationToken]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Ao fechar a página ou sair: encerrar chat em chats_instagram
  useEffect(() => {
    const onPageHide = () => {
      const token = conversationTokenRef.current;
      if (token) closeChatOnServer(token);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [closeChatOnServer]);

  // Cleanup SSE e encerrar chat ao desmontar (ex.: navegação dentro do app)
  useEffect(() => {
    return () => {
      const token = conversationTokenRef.current;
      if (token) closeChatOnServer(token);
      if (sseRef.current) {
        sseRef.current.close();
      }
    };
  }, [closeChatOnServer]);


  // Verificar se deve exibir o widget
  const currentModule = getCurrentModule(currentPath);
  const shouldShow = webchatEnabled && currentModule && modulesEnabled.includes(currentModule);

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      {/* Botão flutuante */}
      {!isOpen && (
        <Button
          onClick={() => {
              prepareNotificationSound();
              setIsOpen(true);
            }}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50"
          size="icon"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}

      {/* Widget */}
      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-96 h-[600px] shadow-2xl z-50 flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg">WebChat</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setIsMinimized(!isMinimized);
                }}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  closeChatOnServer(conversationToken);
                  setIsOpen(false);
                  setIsMinimized(false);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          {!isMinimized && (
            <>
              <CardContent className="flex-1 flex flex-col p-0">
                {/* Status */}
                <div className="px-4 py-2 border-b text-xs text-muted-foreground">
                  {sseConnected ? (
                    <span className="text-green-600">● Conectado</span>
                  ) : (
                    <span className="text-gray-400">● Desconectado</span>
                  )}
                </div>

                {/* Mensagens */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 && (
                      <div className="text-center text-sm text-muted-foreground py-8">
                        Nenhuma mensagem ainda. Envie uma mensagem para começar!
                      </div>
                    )}
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={cn(
                          "flex",
                          msg.sender_type === "contact" ? "justify-end" : "justify-start"
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                            msg.sender_type === "contact"
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted"
                          )}
                        >
                          <FormattedMessage text={msg.content} />
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="border-t p-4">
                  <div className="flex gap-2">
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder="Digite sua mensagem..."
                      disabled={sending || !sseConnected}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={sending || !inputMessage.trim() || !sseConnected}
                      size="icon"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      )}
    </>
  );
}
